import Axios from 'axios'
import CONSTANTS from '../constants'
import { getCSRFToken } from './cookies'

/**
 * @param {object} response
 * @returns {object} response
 */
const setTokenExpireTime = (response) => {
  const date = new Date()
  date.setSeconds(date.getSeconds() + parseInt(response.data.expires_in, 10))
  response.data.expires_at = date.getTime()

  return response
}

/**
 * Execute token request
 *
 * @param {object} postParams request params
 * @returns {*|PromiseLike<T>|Promise<T>}
 */
const getToken = (postParams) => {
  const result = Axios.post(
    `${CONSTANTS.SESSION_API_URL}/token/`,
    postParams,
    {
      'Content-Type': 'x-www-undercoated',
      headers: {
        'X-TO-AuthCookie': CONSTANTS.IS_COOKIE_SETTABLE,
        'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
      },
    },
  ).then(response => setTokenExpireTime(response))

  return result
}

export const api = {
  register(credentials) {
    return Axios.post(
      `${CONSTANTS.SESSION_API_URL}/api/register`,
      {
        ...credentials,
        client_id: CONSTANTS.CLIENT_ID,
        client_secret: CONSTANTS.CLIENT_SECRET,
        locale: CONSTANTS.LOCALE,
        site: CONSTANTS.SITE,
        ...CONSTANTS.MAPP_TRACKING_PARAMETERS,
      },
      {
        'Content-Type': 'application/json',
        headers: {
          'X-TO-AuthCookie': CONSTANTS.IS_COOKIE_SETTABLE,
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
          'X-CSRFToken': getCSRFToken()
        },
      },
    ).then(response => setTokenExpireTime(response))
  },

  login(credentials) {
    return getToken({
      grant_type: 'password',
      username: credentials.email,
      password: credentials.password,
      remember_me: credentials.rememberPassword,
      client_id: CONSTANTS.CLIENT_ID,
      client_secret: CONSTANTS.CLIENT_SECRET,
      locale: CONSTANTS.LOCALE,
      site: CONSTANTS.SITE,
    })
  },

  twitterLogin(twitterCredentials) {
    const payload = {
      ...twitterCredentials,
      ...CONSTANTS.MAPP_TRACKING_PARAMETERS,
      client_id: CONSTANTS.CLIENT_ID,
      provider: 'twitter',
      locale: CONSTANTS.LOCALE,
      site: CONSTANTS.SITE,

    }

    return Axios.post(
      CONSTANTS.SOCIAL_OAUTH2_LOGIN,
      payload,
      {
        headers: {
          'X-CSRFToken': getCSRFToken(),
          'X-TO-AuthCookie': CONSTANTS.IS_COOKIE_SETTABLE,
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
        },
      },
    ).then(response => setTokenExpireTime(response))
  },

  facebookLogin(facebookCredentials) {
    const payload = {
      ...facebookCredentials,
      ...CONSTANTS.MAPP_TRACKING_PARAMETERS,
      client_id: CONSTANTS.CLIENT_ID,
      provider: 'facebook',
      locale: CONSTANTS.LOCALE,
      site: CONSTANTS.SITE,
    }

    return Axios.post(
      CONSTANTS.SOCIAL_OAUTH2_LOGIN,
      payload,
      {
        headers: {
          'X-CSRFToken': getCSRFToken(),
          'X-TO-AuthCookie': CONSTANTS.IS_COOKIE_SETTABLE,
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
        },
      },
    ).then(response => setTokenExpireTime(response))
  },

  googleLogin(googleCredentials) {
    const payload = {
      ...googleCredentials,
      ...CONSTANTS.MAPP_TRACKING_PARAMETERS,
      client_id: CONSTANTS.CLIENT_ID,
      provider: 'google_plus',
      locale: CONSTANTS.LOCALE,
      site: CONSTANTS.SITE,
    }

    return Axios.post(
      CONSTANTS.SOCIAL_OAUTH2_LOGIN,
      payload,
      {
        headers: {
          'X-CSRFToken': getCSRFToken(),
          'X-TO-AuthCookie': CONSTANTS.IS_COOKIE_SETTABLE,
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
        },
      },
    ).then(response => setTokenExpireTime(response))
  },

  logout() {
    return Axios.get(
      `${CONSTANTS.SESSION_API_URL}/api/signout`,
      {
        'Content-Type': 'x-www-undercoated',
        headers: {
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
        },
      },
    )
  },

  resetPassword(email, site_id, platform) {
    return Axios.post(
      `${CONSTANTS.SESSION_API_URL}/api/reset_password`,
      {
        email: email,
        site_id: site_id,
        client_id: CONSTANTS.CLIENT_ID,
        client_secret: CONSTANTS.CLIENT_SECRET,
        platform: platform,
      },
      {
        headers: {
          'X-CSRFToken': getCSRFToken(),
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
        },
      },
    )
  },

  accountInfo() {
    return Axios.post(
      `${CONSTANTS.SESSION_API_URL}/api/user_profile?client_id=${CONSTANTS.CLIENT_ID}`,
      {},
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
          'X-CSRFToken': getCSRFToken()
        },
      },
    )
  },

  updateAccountInfo(params) {
    return Axios.post(
      `${CONSTANTS.SESSION_API_URL}/api/update_account_info?client_id=${CONSTANTS.CLIENT_ID}`,
      params,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
        },
      },
    )
  },

  updatePassword(params) {
    return Axios.post(
      `${CONSTANTS.SESSION_API_URL}/api/update_password`,
      {
        email: params.email,
        password: params.password,
        signature: params.signature,
      },
      {
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
        },
      },
    )
  },

  updatePhoto(params) {
    return Axios.post(
      `${CONSTANTS.SESSION_API_URL}/api/update_photo?client_id=${CONSTANTS.CLIENT_ID}`,
      {
        photo: params.photo,
      },
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
        },
      },
    )
  },

  initPaymentGateway() {
    return Axios.get(
      `${CONSTANTS.CHECKOUT_API_URL}/app/payment_gateway/braintree?market=london&mai=ad-free`,
      {
        withCredentials: false,
        headers: {
          'Content-Type': 'application/json',
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
          'X-YPlan-API-Version': '1.0.0',
        },
      },
    )
  },

  sessionInfo(data = {}) {
    return Axios.post(
      `${CONSTANTS.CHECKOUT_API_URL}/app/session/`,
      data,
      {
        withCredentials: false,
        headers: {
          'Content-Type': 'application/json',
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
          'X-YPlan-API-Version': '1.0.0',
        },
      },
    )
  },

  payAdFreeSubscription(data) {
    const {apiKey, ...rest} = data;
    if (!apiKey) throw new Error('invalid credentials provided');

    return Axios.post(
      `${CONSTANTS.CHECKOUT_API_URL}/app/ad_free_subscription`,
      rest,
      {
        withCredentials: false,
        headers: {
          'X-YPlan-Vendor-Identifier': CONSTANTS.DEVICE_ID,
          'X-YPlan-API-Version': '1.0.0',
          'Authorization': apiKey
        },
      },
    )
  },

  /**
   * Error response can have different structure
   *  - {"errors": ["message"]} - custom response message
   *  - {"errors": {"field1": ["message"], "field2": ["message"]}} - serialiser validation
   * @param responseData
   * @returns {string}
   */
  stringifyResponseErrors(responseData) {
    if (Object.hasOwnProperty.call(responseData, 'errors')) {
      if (Array.isArray(responseData.errors)) {
        return responseData.errors[0]
      }
      if (Object.keys(responseData.errors).length > 0) {
        const messages = Object.keys(responseData.errors)
          .map(key => `${key}: ${responseData.errors[key]}`)
        return messages.join('<br>')
      }
    }

    return ''
  },
}

export default api



// WEBPACK FOOTER //
// ./src/services/api.js